"use client"

import { useState, useEffect, lazy, Suspense } from "react"
import { Routes, Route, useLocation, Navigate, useParams } from "react-router-dom"
import { Layout, App as AntdApp, Spin } from "antd"
import { AnimatePresence } from "framer-motion"
import { setNotificationApi } from "./utils/notificationService"

// Components (keep static — small, used on every page)
import Header from "./components/layout/Header"
import Footer from "./components/layout/Footer"
import UpdateNotification from "./components/UpdateNotification"
import Seo from "./components/Seo"
import ImpersonationBanner from "./components/ImpersonationBanner"
import RouteIndexingPolicy from "./components/RouteIndexingPolicy"

// Layouts (keep static — referenced in every authenticated route)
import CustomerLayout from "./components/layout/CustomerLayout"
import AdminLayout from "./components/layout/AdminLayout"

// Context providers (keep static)
import { AuthProvider } from "./contexts/AuthContext"
import { CartProvider } from "./contexts/CartContext"
import { WalletProvider } from "./contexts/WalletContext"
import { OrderProvider } from "./contexts/OrderContext"
import { TransactionProvider} from "./contexts/TransactionContext"
import { DataProvider } from "./contexts/DataContext"
import { CurrencyProvider } from "./contexts/CurrencyContext"

// Route guard (keep static)
import { ProtectedRoute } from "./components/ProtectedRoute"

// Public pages — lazy loaded
const HomePage = lazy(() => import("./pages/HomePage"))
const LoginPage = lazy(() => import("./pages/LoginPage"))
const RegisterPage = lazy(() => import("./pages/RegisterPage"))
const ProductListPage = lazy(() => import("./pages/ProductListPage"))
const CategoryListPage = lazy(() => import("./pages/CategoryListPage"))
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"))
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"))
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"))
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"))
const SecurityPage = lazy(() => import("./pages/SecurityPage"))
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"))
const ReceiptPage = lazy(() => import("./pages/ReceiptPage"))

// Account pages — lazy loaded
const DashboardPage = lazy(() => import("./pages/account/DashboardPage"))
const OrdersPage = lazy(() => import("./pages/account/OrdersPage"))
const TransactionsPage = lazy(() => import("./pages/account/TransactionsPage"))
const AccountDetailsPage = lazy(() => import("./pages/account/AccountDetailsPage"))
const PaymentsPage = lazy(() => import("./pages/account/PaymentsPage"))
const EmailPreferencesPage = lazy(() => import("./pages/account/EmailPreferencesPage"))

// Admin pages — lazy loaded
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"))
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"))
const ProductsManagement = lazy(() => import("./pages/admin/ProductsManagement"))
const CategoriesManagement = lazy(() => import("./pages/admin/CategoriesManagement"))
const NavSectionsManagement = lazy(() => import("./pages/admin/NavSectionsManagement"))
const NavSectionPage = lazy(() => import("./pages/NavSectionPage"))
const CategoryShopPage = lazy(() => import("./pages/CategoryShopPage"))
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"))
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"))
const ProvidersManagement = lazy(() => import("./pages/admin/ProvidersManagement"))
const OrdersManagement = lazy(() => import("./pages/admin/OrdersManagement"))
const TransactionsManagement = lazy(() => import("./pages/admin/TransactionsManagement"))
const SystemSettings = lazy(() => import("./pages/admin/SystemSettings"))
const SystemBackups = lazy(() => import("./pages/admin/SystemBackups"))
const RecurringPaymentsManagement = lazy(() => import("./pages/admin/RecurringPaymentsManagement"))
const SavedCardsManagement = lazy(() => import("./pages/admin/SavedCardsManagement"))
const ImpersonationLog = lazy(() => import("./pages/admin/ImpersonationLog"))
const ApiKeysManagement = lazy(() => import("./pages/admin/ApiKeysManagement"))
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"))
const BlogEditor = lazy(() => import("./pages/admin/BlogEditor"))
const BlogListPage = lazy(() => import("./pages/BlogListPage"))
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"))
const NewslettersManagement = lazy(() => import("./pages/admin/NewslettersManagement"))
const NewsletterEditor = lazy(() => import("./pages/admin/NewsletterEditor"))
const MarketingDashboard = lazy(() => import("./pages/admin/MarketingDashboard"))
const ReportsArchive = lazy(() => import("./pages/admin/ReportsArchive"))
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"))

const { Content } = Layout

// Tiny redirect helper for /news/:slug → /blog/:slug (legacy URL support)
const NewsToBlogRedirect = () => {
  const { slug } = useParams()
  return <Navigate to={`/blog/${slug}`} replace />
}

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
    <Spin size="large" />
  </div>
)

// Registers the context-aware antd notification API so non-component utilities
// (e.g. apiRequest.js) can show notifications that respect the active theme.
const NotificationInitializer = () => {
  const { notification } = AntdApp.useApp()
  useEffect(() => {
    setNotificationApi(notification)
  }, [notification])
  return null
}

const App = () => {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for updates on app start
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update()
      })
    }

    // Check for updates every hour
    const updateInterval = setInterval(() => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update()
        })
      }
    }, 60 * 60 * 1000) // Check every hour

    const loadingTimer = setTimeout(() => {
      setIsLoading(false)
    }, 500)

    // Cleanup function
    return () => {
      clearTimeout(loadingTimer)
      clearInterval(updateInterval)
    }
  }, []) // Empty dependency array means this runs once on mount

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="logo-container">
          <img src="/logo.png" alt="Tanaka Life" className="loading-logo" />
        </div>
      </div>
    )
  }

  return (
    <AntdApp>
      {/* Site-wide default head tags (title/description/OG/Twitter). Pages that render
          their own <Seo> override these per-property via react-helmet-async. */}
      <Seo
        title="Instant Airtime, Data Bundles & ZESA Tokens"
        description="Buy instant airtime, data bundles & ZESA tokens for Zimbabwe. Services for diaspora & SA customers. Secure payments with Vouchers, Mastercard, Visa & Instant EFT."
      />
      <NotificationInitializer />
      <CurrencyProvider>
      <AuthProvider>
        {/* Inside AuthProvider (needs useAuth) and outside <Routes> so it shows on
            every page — an admin must never lose sight of whose account they are in. */}
        <ImpersonationBanner />
        <DataProvider>
        <WalletProvider>
          <CartProvider>
            <OrderProvider>
              <TransactionProvider>
                <Suspense fallback={<PageLoader />}>
                  <AnimatePresence mode="wait">
                    <Routes location={location} key={location.pathname}>
                      {/* Public routes with traditional layout */}
                      <Route path="/" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <HomePage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/login" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <LoginPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/register" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <RegisterPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/products" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <ProductListPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      {/* Network/category landing pages, e.g. /products/econet — SEO entry points */}
                      <Route path="/products/:category" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <ProductListPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/categories" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <CategoryListPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/product/:id" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <ProductDetailPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      {/* Category-as-product (isconv): categories with no child products
                          ARE the sellable item — ZESA tokens, Nyaradzo, variable airtime.
                          This clean path is their canonical URL; /product/:id?conv=true
                          still works as an alias. Unlike a query-string URL this can be
                          prerendered, so these pages get crawlable HTML and link previews. */}
                      <Route path="/buy/:slug" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <ProductDetailPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/terms-of-service" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <TermsAndConditions />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/privacy-policy" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <PrivacyPolicy />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/security" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <SecurityPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      <Route path="/blog" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <BlogListPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                      <Route path="/blog/:slug" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <BlogPostPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      {/* Storefront navigation landing pages — one crawlable URL per
                          network tab and per section, so the taxonomy is indexable
                          rather than being client-side tab state only. */}
                      <Route path="/network/:slug" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content"><NavSectionPage /></Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      {/* Cross-network category listings (all Airtime, all Data).
                          The taxonomy is stored network-first, so these have no
                          section row of their own — the backend composes them.
                          Where the mobile home screen's cards land, but not
                          mobile-only: a shared link must work anywhere. */}
                      <Route path="/shop/:type" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content"><CategoryShopPage /></Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      {/* Standalone order tracking. HomePage's search-track-card
                          has always had an inline "Track Order" tab (no route
                          of its own); this is what the mobile quick-actions
                          "Track Order" card links to. It previously pointed at
                          /trackorder, which is the BACKEND api endpoint, not a
                          frontend route — React Router had nothing to match
                          and fell through to the 404 page. */}
                      <Route path="/track-order" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content"><TrackOrderPage /></Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      {/* Product search results. The mobile search bar's Enter
                          used to navigate to /products/search — the BACKEND api
                          path, not a route — which 404'd every time. */}
                      <Route path="/search" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content"><SearchResultsPage /></Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      {/* Public, token-scoped receipt (guest-safe; opened via link or QR) */}
                      <Route path="/receipt/:token" element={<ReceiptPage />} />

                      {/* One-click unsubscribe from an email footer. The second path
                          catches links already sent out, which pointed at /api/... on
                          this host and rendered the 404 page; the static host has no
                          real /api, so that path only ever reaches the SPA anyway. */}
                      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
                      <Route path="/api/unsubscribe/:token" element={<UnsubscribePage />} />

                      {/* Legacy /news redirects (kept for inbound links and bookmarks) */}
                      <Route path="/news" element={<Navigate to="/blog" replace />} />
                      <Route path="/news/:slug" element={<NewsToBlogRedirect />} />

                      {/* Redirect old account route to dashboard */}
                      <Route path="/account" element={<Navigate to="/account/dashboard" replace />} />

                      {/* Authenticated routes with sidebar layout */}
                      <Route path="/account/dashboard" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <DashboardPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/account/orders" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <OrdersPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/account/transactions" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <TransactionsPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/account/details" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <AccountDetailsPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/account/payments" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <PaymentsPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/account/email-preferences" element={
                        <ProtectedRoute roles={['customer','admin']}>
                          <CustomerLayout>
                            <EmailPreferencesPage />
                          </CustomerLayout>
                        </ProtectedRoute>
                      } />

                      {/* Admin routes with admin layout */}
                      <Route path="/admin/dashboard" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <AdminDashboard />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/users" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <UsersManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/products" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <ProductsManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/categories" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <CategoriesManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      {/* Storefront navigation (network tabs) — owned by Tanaka,
                          not the Hot Recharge sync. Drives the home page and the bot. */}
                      <Route path="/admin/navigation" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <NavSectionsManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/providers" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <ProvidersManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/orders" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <OrdersManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/transactions" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <TransactionsManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/settings" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <SystemSettings />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/backups" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <SystemBackups />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/recurring-payments" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <RecurringPaymentsManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/saved-cards" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <SavedCardsManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/impersonation-log" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <ImpersonationLog />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/reports" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <ReportsArchive />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/api-keys" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <ApiKeysManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/blog" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <BlogManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/blog/new" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <BlogEditor />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/blog/edit/:id" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <BlogEditor />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      {/* Legacy /admin/news redirects */}
                      <Route path="/admin/news" element={<Navigate to="/admin/blog" replace />} />
                      <Route path="/admin/news/new" element={<Navigate to="/admin/blog/new" replace />} />
                      <Route path="/admin/news/edit/:id" element={<Navigate to="/admin/blog" replace />} />

                      <Route path="/admin/newsletters" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <NewslettersManagement />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/newsletters/new" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <NewsletterEditor />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/newsletters/edit/:id" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <NewsletterEditor />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />

                      <Route path="/admin/marketing" element={
                        <ProtectedRoute roles={['admin']}>
                          <AdminLayout>
                            <MarketingDashboard />
                          </AdminLayout>
                        </ProtectedRoute>
                      } />


                      {/* Checkout with header/footer but no sidebar - allows guest users */}
                      <Route path="/checkout" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <CheckoutPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />

                      <Route path="*" element={
                        <Layout className="layout">
                          <Header />
                          <Content className="content" style={{ marginTop: "-19px" }}>
                            <NotFoundPage />
                          </Content>
                          <Footer />
                          <UpdateNotification />
                        </Layout>
                      } />
                    </Routes>
                    {/* Rendered after <Routes> so its noindex wins over any page-level
                        <Seo> on private/transactional routes. */}
                    <RouteIndexingPolicy />
                  </AnimatePresence>
                </Suspense>
               </TransactionProvider>
            </OrderProvider>
          </CartProvider>
        </WalletProvider>
        </DataProvider>
      </AuthProvider>
      </CurrencyProvider>
    </AntdApp>
  )
}

export default App
